/* ============================================
   AGU BABY V2 — Animation Controller
   ScrollAnimator, ParallaxLite, Confetti
   Zero dependencies, hardware-accelerated
   ============================================ */

(function() {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* =============================================
     ScrollAnimator — IntersectionObserver-based
     Adds .is-visible class with stagger delays
     ============================================= */
  window.ScrollAnimator = {
    observer: null,

    init: function() {
      if (prefersReducedMotion) {
        // Instantly show everything
        document.querySelectorAll('.animate-on-scroll').forEach(function(el) {
          el.classList.add('is-visible');
        });
        return;
      }

      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px'
      });

      this.observeAll();
    },

    observeAll: function() {
      var elements = document.querySelectorAll('.animate-on-scroll:not(.is-visible)');
      elements.forEach(function(el) {
        this.observer.observe(el);
      }.bind(this));
    },

    handleIntersect: function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;

        var el = entry.target;
        var staggerIndex = parseInt(el.dataset.staggerIndex) || 0;
        var delay = staggerIndex * 80; // matches --stagger-delay

        if (delay > 0) {
          el.style.transitionDelay = delay + 'ms';
        }

        // Use rAF for smooth visual update
        requestAnimationFrame(function() {
          el.classList.add('is-visible');
        });

        // Unobserve after triggering (one-time)
        this.observer.unobserve(el);
      }.bind(this));
    }
  };

  /* =============================================
     ParallaxLite — Transform-based, desktop only
     ============================================= */
  window.ParallaxLite = {
    elements: [],
    isDesktop: false,
    rafId: null,

    init: function() {
      if (prefersReducedMotion) return;

      this.isDesktop = window.matchMedia('(min-width: 1001px)').matches;
      if (!this.isDesktop) return;

      var els = document.querySelectorAll('[data-parallax]');
      els.forEach(function(el) {
        this.elements.push({
          el: el,
          speed: parseFloat(el.dataset.parallax) || 0.15
        });
      }.bind(this));

      if (this.elements.length > 0) {
        this.tick();
      }

      // Auto-destroy on resize below desktop
      window.matchMedia('(min-width: 1001px)').addEventListener('change', function(e) {
        if (!e.matches) {
          this.destroy();
        }
      }.bind(this));
    },

    tick: function() {
      var scrollY = window.scrollY;
      var viewportHeight = window.innerHeight;

      this.elements.forEach(function(item) {
        var rect = item.el.getBoundingClientRect();
        var elementCenter = rect.top + rect.height / 2;
        var viewportCenter = viewportHeight / 2;
        var offset = (elementCenter - viewportCenter) * item.speed;

        item.el.style.transform = 'translate3d(0, ' + offset + 'px, 0)';
      });

      this.rafId = requestAnimationFrame(this.tick.bind(this));
    },

    destroy: function() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.elements.forEach(function(item) {
        item.el.style.transform = '';
      });
      this.elements = [];
    }
  };

  /* =============================================
     Confetti Pop — Burst of colored particles
     ============================================= */
  window.confettiPop = function(originX, originY) {
    if (prefersReducedMotion) return;

    var colors = ['#E8637A', '#4DBFA5', '#F2A83B', '#8B6FC0', '#5AABE0'];
    var particleCount = 10;

    for (var i = 0; i < particleCount; i++) {
      var particle = document.createElement('div');
      particle.className = 'confetti-particle';
      particle.style.left = originX + 'px';
      particle.style.top = originY + 'px';
      particle.style.background = colors[i % colors.length];

      // Random direction
      var angle = (Math.PI * 2 / particleCount) * i + (Math.random() * 0.5 - 0.25);
      var velocity = 30 + Math.random() * 40;
      var x = Math.cos(angle) * velocity;
      var y = Math.sin(angle) * velocity - 20; // bias upward

      particle.style.setProperty('--x', x + 'px');
      particle.style.setProperty('--y', y + 'px');

      // Random shape (circle or square)
      if (Math.random() > 0.5) {
        particle.style.borderRadius = '0';
        particle.style.width = '6px';
        particle.style.height = '10px';
      }

      document.body.appendChild(particle);

      // Auto-cleanup
      setTimeout(function(p) {
        if (p.parentNode) p.parentNode.removeChild(p);
      }, 900, particle);
    }
  };

  /* =============================================
     AnimatedCounter — Eased count animation
     ============================================= */
  window.animateCounter = function(element, targetValue, duration) {
    if (prefersReducedMotion) {
      element.textContent = targetValue;
      return;
    }

    duration = duration || 400;
    var startValue = parseInt(element.textContent) || 0;
    var startTime = null;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var progress = Math.min((timestamp - startTime) / duration, 1);
      // Ease out cubic
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.round(startValue + (targetValue - startValue) * eased);
      element.textContent = current;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  };

  /* =============================================
     SwipeCarousel — Dot indicator sync
     ============================================= */
  window.SwipeCarousel = {
    init: function() {
      var carousels = document.querySelectorAll('[data-mobile-carousel]');
      carousels.forEach(function(carousel) {
        this.setup(carousel);
      }.bind(this));
    },

    setup: function(container) {
      var items = container.children;
      if (items.length < 2) return;

      // Create dot container
      var dotsContainer = document.createElement('div');
      dotsContainer.className = 'carousel-dots';

      for (var i = 0; i < items.length; i++) {
        var dot = document.createElement('button');
        dot.className = 'carousel-dot' + (i === 0 ? ' active' : '');
        dot.dataset.index = i;
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dotsContainer.appendChild(dot);
      }

      container.parentNode.insertBefore(dotsContainer, container.nextSibling);

      // Sync dots on scroll
      var dots = dotsContainer.querySelectorAll('.carousel-dot');
      container.addEventListener('scroll', function() {
        var scrollLeft = container.scrollLeft;
        var itemWidth = items[0].offsetWidth + 12; // gap
        var activeIndex = Math.round(scrollLeft / itemWidth);

        dots.forEach(function(d, idx) {
          d.classList.toggle('active', idx === activeIndex);
        });
      }, { passive: true });

      // Dot click
      dotsContainer.addEventListener('click', function(e) {
        if (!e.target.classList.contains('carousel-dot')) return;
        var idx = parseInt(e.target.dataset.index);
        var itemWidth = items[0].offsetWidth + 12;
        container.scrollTo({ left: idx * itemWidth, behavior: 'smooth' });
      });
    }
  };

})();
